<template>
    <div>
        <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }" v-if="editable">
            <div>
                <b-button-toolbar>
                    <b-button-group size="sm" class="mx-1">
                        <b-button variant="outline-secondary" :pressed="false" @click="commands.undo">
                            <font-awesome-icon class="fa-fw" icon="reply" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="false" @click="commands.redo">
                            <font-awesome-icon class="fa-fw" icon="share" />
                        </b-button>
                    </b-button-group>
                    <b-button-group size="sm" class="mx-1">
                        <b-button variant="outline-secondary" :pressed="isActive.bold()" @click="commands.bold">
                            <font-awesome-icon class="fa-fw" icon="bold" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.italic()" @click="commands.italic">
                            <font-awesome-icon class="fa-fw" icon="italic" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.strike()" @click="commands.strike">
                            <font-awesome-icon class="fa-fw" icon="strikethrough" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.underline()" @click="commands.underline">
                            <font-awesome-icon class="fa-fw" icon="underline" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.code()" @click="commands.code">
                            <font-awesome-icon class="fa-fw" icon="code" />
                        </b-button>
                    </b-button-group>
                    <b-button-group size="sm" class="mx-1">
                        <b-button variant="outline-secondary" :pressed="isActive.paragraph()" @click="commands.paragraph">
                            <font-awesome-icon class="fa-fw" icon="paragraph" />
                        </b-button>
                        <b-dropdown variant="outline-secondary" size="sm" :toggle-class="{ active: isActive.heading({ level: 1 }) || isActive.heading({ level: 2 }) || isActive.heading({ level: 3 }) }" no-caret>
                            <template #button-content>
                                <font-awesome-icon class="fa-fw" icon="heading" />
                            </template>
                            <b-dropdown-item :active="isActive.heading({ level: 1 })" @click="commands.heading({ level: 1 })"><font-awesome-icon class="fa-fw" icon="h1" /></b-dropdown-item>
                            <b-dropdown-item :active="isActive.heading({ level: 2 })" @click="commands.heading({ level: 2 })"><font-awesome-icon class="fa-fw" icon="h2" /></b-dropdown-item>
                            <b-dropdown-item :active="isActive.heading({ level: 3 })" @click="commands.heading({ level: 3 })"><font-awesome-icon class="fa-fw" icon="h3" /></b-dropdown-item>
                        </b-dropdown>
                        <b-button variant="outline-secondary" :pressed="isActive.bullet_list()" @click="commands.paragraph(); commands.bullet_list();">
                            <font-awesome-icon class="fa-fw" icon="list-ul" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.ordered_list()" @click="commands.paragraph(); commands.ordered_list();">
                            <font-awesome-icon class="fa-fw" icon="list-ol" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.todo_list()" @click="commands.todo_list">
                            <font-awesome-icon class="fa-fw" icon="tasks" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.blockquote()" @click="commands.blockquote">
                            <font-awesome-icon class="fa-fw" icon="quote-right" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.code_block()" @click="commands.code_block">
                            <font-awesome-icon class="fa-fw" icon="code" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.iframe()" @click="commands.iframe">
                            <font-awesome-icon class="fa-fw" icon="photo-video" />
                        </b-button>
                    </b-button-group>
                    <b-button-group size="sm" class="mx-1">
                        <b-button variant="outline-secondary" :pressed="isActive.input()" @click="commands.input">
                            <font-awesome-icon class="fa-fw" icon="text" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.check_group()" @click="commands.check_group">
                            <font-awesome-icon class="fa-fw" icon="check-square" />
                        </b-button>
                        <b-button variant="outline-secondary" :pressed="isActive.select_box()" @click="commands.select_box">
                            <font-awesome-icon class="fa-fw" icon="ballot-check" />
                        </b-button>
                    </b-button-group>
                </b-button-toolbar>
            </div>
        </editor-menu-bar>
        <editor-content
            class="editor__content"
            :editor="editor"
            :content="value"
            />
    </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, Extension } from 'tiptap'
import {
    Blockquote,
    CodeBlock,
    HardBreak,
    Heading,
    OrderedList,
    BulletList,
    ListItem,
    TodoItem,
    TodoList,
    Bold,
    Code,
    Italic,
    Link,
    Strike,
    Underline,
    History,
} from 'tiptap-extensions'
import Iframe from '@/libs/tiptap/extensions/Iframe.js'
import Input from '@/libs/tiptap/extensions/Input.js'
import SelectBox from '@/libs/tiptap/extensions/SelectBox.js'
import CheckGroup from '@/libs/tiptap/extensions/CheckGroup.js'
//import RadioItem from '@/libs/tiptap/extensions/RadioItem.js'
//import RadioList from '@/libs/tiptap/extensions/RadioList.js'

export default {
    components: {
        EditorContent,
        EditorMenuBar,
    },
    props: {
        value: {
            default: '',
            type: String
        },
        editable: {
            default: true,
            type: Boolean
        }
    },
    data() {
        return {
            editor: null,
        }
    },
    watch: {
        editor: {
            immediate: true,
            handler(editor) {
                if (!editor || !editor.element) return;
                this.editor.setContent(this.value);
                this.editor.on('update', ({ getHTML }) => {
                    this.$emit('input', getHTML());
                });
            }
        },
        value: {
            handler(value) {
                if (value !== this.editor.getHTML()) {
                    this.editor.setContent(value);
                }
            }
        }
    },
    methods: {
        onUpdate(getHTML) {
            this.$emit('input', this.editor.getHTML())
        }
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                new Blockquote(),
                new CodeBlock(),
                new HardBreak(),
                new Heading({ levels: [1, 2, 3] }),
                new BulletList(),
                new OrderedList(),
                new ListItem(),
                new TodoItem({
                    nested: true,
                }),
                new TodoList(),
                new Bold(),
                new Code(),
                new Italic(),
                new Link(),
                new Strike(),
                new Underline(),
                new History(),

                new Iframe(),
                //new SelectItem(),
                new Input(),
                new SelectBox(),
                new CheckGroup(),
                //new RadioItem({
                //    nested: true,
                //}),
                //new RadioList(),
            ],
            content: this.value,
            editable: this.editable,
            onUpdate: (e) => {
                this.$emit('onUpdate', e)
            },
            onInit: (e) => {
                this.$emit('onInit', e)
            },
        })
    },
    beforeDestro() {
        this.editor.destroy()
    },
};
</script>

<style lang="scss" type="text/scss" scoped>
.ProseMirror {
    box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
    border: 1px solid #dbdbdb;
    display: block;
    max-width: 100%;
    min-width: 100%;
    padding: 0.625em;
    resize: vertical;
    background-color: white;
    border-radius: 4px;
}
</style>
